import { i18n } from '@marine/i18n';
import { type OrganizationMember, organizationMemberName } from '../organizations/organizationMembers';
import { type CurrentUser, toUserIdText } from '../rest-api/users/currentUserApi';

/**
 * How a task names someone no longer in its organization — never their bare id. Read when it is
 * shown, so it is in the language the user is in.
 */
export const formerMemberLabel = () => i18n.t('tasks:assignee.formerMember');

/**
 * The user ids in a task's `assignee_ids`, in order, each once.
 *
 * This app writes `["12","34"]`; rows the legacy backend wrote hold `"12","34"` without the brackets.
 * Every run of digits is an id, which is how the backend reads both (`DueAlertSender`), so the two
 * can never disagree about who a task names. A bare `"12"` reads as `["12"]` too, which lets a picker
 * holding a single id ask the same questions of it.
 */
export const parseAssigneeIds = (value: string | null | undefined): string[] => [
  ...new Set(value?.match(/\d+/g) ?? []),
];

/**
 * The `assignee_ids` to write for these ids: `["12"]`, user ids as JSON strings, which is what the
 * backend stores and what its policy and list filter match on (`"12"`). `null` for nobody — the
 * backend turns an empty list into `null` anyway, and a `LIKE` filter for "unassigned" stays simple.
 */
export const serializeAssigneeIds = (ids: readonly string[]): string | null => {
  const unique = [...new Set(ids.filter(Boolean))];

  return unique.length > 0 ? JSON.stringify(unique) : null;
};

export type TaskAssignee = {
  id: string;
  /** `null` while the organization's members are still loading, or offline. */
  name: string | null;
  isMe: boolean;
  /** Named on the task but no longer in its organization; the API leaves an unchanged one alone. */
  isFormerMember: boolean;
};

type ResolveTaskAssigneesInput = {
  currentUser: CurrentUser | null | undefined;
  /** `undefined` until the members list arrives — it is online only, see `useOrganizationMembersQuery`. */
  members: readonly OrganizationMember[] | undefined;
};

/**
 * Who a task's assignees are, looked up the way the pickers offer them: the user, then the
 * organization's members. Someone the members list doesn't hold is a former member — but only once
 * the list is there to say so: until then their name is `null`, not a wrong "Former member".
 */
export const resolveTaskAssignees = (
  assigneeIds: string | null | undefined,
  { currentUser, members }: ResolveTaskAssigneesInput,
): TaskAssignee[] => {
  const currentUserId = toUserIdText(currentUser);

  return parseAssigneeIds(assigneeIds).map((id) => {
    if (id === currentUserId) {
      const name = [currentUser?.name, currentUser?.surname].filter(Boolean).join(' ').trim();

      return { id, name: name || i18n.t('tasks:assignee.me'), isMe: true, isFormerMember: false };
    }

    if (!members) {
      return { id, name: null, isMe: false, isFormerMember: false };
    }

    const member = members.find((candidate) => String(candidate.id) === id);

    return member
      ? { id, name: organizationMemberName(member), isMe: false, isFormerMember: false }
      : { id, name: formerMemberLabel(), isMe: false, isFormerMember: true };
  });
};
